import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';
import { Calendar, Activity, Target, TrendingUp, ChevronLeft, ChevronRight, Heart, Timer } from 'lucide-react';

interface TrainingCalendarProps {
  timeRange: string;
}

export const TrainingCalendar: React.FC<TrainingCalendarProps> = ({ timeRange }) => {
  const { user } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedRunData, setSelectedRunData] = useState<any>(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const { data: calendarData, isLoading } = useQuery({
    queryKey: ['training-calendar', timeRange],
    queryFn: async () => {
      if (!user) return null;

      const dateLimit = getDateLimit(timeRange);
      let query = supabase
        .from('running_performance')
        .select('*')
        .order('recorded_date', { ascending: true });

      if (dateLimit) {
        query = query.gte('recorded_date', dateLimit);
      }

      const { data: runs, error } = await query;
      if (error) throw error;

      // Process data for calendar view
      const dailyData = new Map();
      const weeklyData = new Map();
      const monthlyData = new Map();

      runs?.forEach(run => {
        const date = run.recorded_date;
        const week = getWeekKey(new Date(date));
        const month = getMonthKey(new Date(date));

        // Daily aggregation
        if (!dailyData.has(date)) {
          dailyData.set(date, {
            date,
            runs: [],
            totalDistance: 0,
            totalTime: 0,
            avgPace: 0,
            maxIntensity: 'easy',
          });
        }

        const dayData = dailyData.get(date);
        dayData.runs.push(run);
        dayData.totalDistance += run.distance_meters || 0;
        dayData.totalTime += run.time_seconds || 0;
        
        // Determine max intensity for the day
        const intensityOrder = ['easy', 'tempo', 'interval', 'race'];
        const runIntensity = run.run_type || 'easy';
        if (intensityOrder.indexOf(runIntensity) > intensityOrder.indexOf(dayData.maxIntensity)) {
          dayData.maxIntensity = runIntensity;
        }

        // Weekly aggregation
        if (!weeklyData.has(week)) {
          weeklyData.set(week, {
            week,
            totalDistance: 0,
            totalTime: 0,
            runCount: 0,
            intensityDistribution: { easy: 0, tempo: 0, interval: 0, race: 0 },
          });
        }

        const weekData = weeklyData.get(week);
        weekData.totalDistance += run.distance_meters || 0;
        weekData.totalTime += run.time_seconds || 0;
        weekData.runCount += 1;
        weekData.intensityDistribution[runIntensity] += 1;

        // Monthly aggregation
        if (!monthlyData.has(month)) {
          monthlyData.set(month, {
            month,
            totalDistance: 0,
            totalTime: 0,
            runCount: 0,
            avgWeeklyDistance: 0,
          });
        }

        const monthData = monthlyData.get(month);
        monthData.totalDistance += run.distance_meters || 0;
        monthData.totalTime += run.time_seconds || 0;
        monthData.runCount += 1;
      });

      // Calculate average pace for daily data
      dailyData.forEach(day => {
        if (day.totalTime > 0 && day.totalDistance > 0) {
          day.avgPace = Math.round((day.totalTime * 1000) / day.totalDistance);
        }
      });

      // Calculate weekly averages for monthly data
      monthlyData.forEach(month => {
        const weeksInMonth = Math.ceil(month.runCount / 7);
        month.avgWeeklyDistance = weeksInMonth > 0 ? month.totalDistance / weeksInMonth : 0;
      });

      return {
        daily: Array.from(dailyData.values()),
        weekly: Array.from(weeklyData.values()),
        monthly: Array.from(monthlyData.values()),
        totalRuns: runs?.length || 0,
        totalDistance: runs?.reduce((sum, run) => sum + (run.distance_meters || 0), 0) || 0,
        totalTime: runs?.reduce((sum, run) => sum + (run.time_seconds || 0), 0) || 0,
      };
    },
    enabled: !!user,
  });

  const getDateLimit = (range: string) => {
    const now = new Date();
    switch (range) {
      case '1m': return new Date(now.setMonth(now.getMonth() - 1)).toISOString().split('T')[0];
      case '3m': return new Date(now.setMonth(now.getMonth() - 3)).toISOString().split('T')[0];
      case '6m': return new Date(now.setMonth(now.getMonth() - 6)).toISOString().split('T')[0];
      case '1y': return new Date(now.setFullYear(now.getFullYear() - 1)).toISOString().split('T')[0];
      default: return null;
    }
  };

  const getWeekKey = (date: Date): string => {
    const year = date.getFullYear();
    const week = getWeekNumber(date);
    return `${year}-W${week}`;
  };

  const getMonthKey = (date: Date): string => {
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
  };

  const getWeekNumber = (date: Date): number => {
    const firstDay = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date.getTime() - firstDay.getTime()) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDay.getDay() + 1) / 7);
  };

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const formatDistance = (meters: number): string => {
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(1)}km`;
    }
    return `${meters}m`;
  };

  const getIntensityColor = (intensity: string): string => {
    switch (intensity) {
      case 'easy': return 'bg-green-100 text-green-800';
      case 'tempo': return 'bg-orange-100 text-orange-800';
      case 'interval': return 'bg-red-100 text-red-800';
      case 'race': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const generateMonthCalendarGrid = () => {
    if (!calendarData?.daily) return [];

    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // First day of the current month
    const firstDayOfMonth = new Date(year, month, 1);
    // Last day of the current month
    const lastDayOfMonth = new Date(year, month + 1, 0);
    
    // Find the start of the calendar grid (Sunday of the week containing the 1st)
    const startDate = new Date(firstDayOfMonth);
    startDate.setDate(startDate.getDate() - startDate.getDay());
    
    // Find the end of the calendar grid (Saturday of the week containing the last day)
    const endDate = new Date(lastDayOfMonth);
    endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));

    const days = [];
    const current = new Date(startDate);
    const today = new Date();

    while (current <= endDate) {
      const dateStr = current.toISOString().split('T')[0];
      const dayData = calendarData.daily.find(d => d.date === dateStr);
      const isCurrentMonth = current.getMonth() === month;
      
      days.push({
        date: dateStr,
        day: current.getDate(),
        isToday: dateStr === today.toISOString().split('T')[0],
        isCurrentMonth,
        data: dayData,
      });

      current.setDate(current.getDate() + 1);
    }

    return days;
  };

  const handleRunClick = (run: any, dayData: any) => {
    setSelectedRunData({
      run,
      dayData,
      date: dayData.date
    });
    setIsPopupOpen(true);
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + (direction === 'next' ? 1 : -1));
      return newDate;
    });
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Training Calendar</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-96 flex items-center justify-center">Loading training calendar...</div>
        </CardContent>
      </Card>
    );
  }

  if (!calendarData?.totalRuns) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Training Calendar</CardTitle>
          <CardDescription>Visual overview of your training schedule</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-96 flex flex-col items-center justify-center text-center">
            <Calendar className="h-16 w-16 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Training Data</h3>
            <p className="text-muted-foreground">Start logging runs to see your training calendar</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const calendarDays = generateMonthCalendarGrid();
  const monthYearStr = currentMonth.toLocaleDateString('en-US', { 
    month: 'long', 
    year: 'numeric' 
  });

  return (
    <div className="space-y-6">
      {/* Summary Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <Activity className="h-8 w-8 mx-auto mb-2 text-primary" />
            <div className="text-2xl font-bold">{calendarData.totalRuns}</div>
            <div className="text-sm text-muted-foreground">Total Runs</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 text-center">
            <Target className="h-8 w-8 mx-auto mb-2 text-blue-600" />
            <div className="text-2xl font-bold">
              {formatDistance(calendarData.totalDistance)}
            </div>
            <div className="text-sm text-muted-foreground">Total Distance</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 text-center">
            <TrendingUp className="h-8 w-8 mx-auto mb-2 text-green-600" />
            <div className="text-2xl font-bold">
              {formatTime(calendarData.totalTime)}
            </div>
            <div className="text-sm text-muted-foreground">Total Time</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 text-center">
            <Calendar className="h-8 w-8 mx-auto mb-2 text-purple-600" />
            <div className="text-2xl font-bold">
              {Math.round(calendarData.totalRuns / (calendarData.daily.length / 7 || 1) * 10) / 10}
            </div>
            <div className="text-sm text-muted-foreground">Runs/Week</div>
          </CardContent>
        </Card>
      </div>

      {/* Calendar View */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Training Calendar</CardTitle>
              <CardDescription>Click on a run to see detailed information</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateMonth('prev')}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="text-lg font-semibold min-w-[140px] text-center">
                {monthYearStr}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateMonth('next')}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-7 gap-2 mb-4">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center font-semibold text-sm p-2">
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-2">
            {calendarDays.map((day) => (
              <div
                key={day.date}
                className={`
                  p-2 min-h-[100px] border rounded-lg transition-colors relative
                  ${day.isToday ? 'ring-2 ring-primary' : ''}
                  ${day.isCurrentMonth ? 'bg-background' : 'bg-muted/30'}
                  ${day.data ? 'border-primary/30' : 'border-border'}
                `}
              >
                <div className={`text-sm font-medium mb-1 ${day.isCurrentMonth ? '' : 'text-muted-foreground'}`}>
                  {day.day}
                </div>
                
                {day.data && (
                  <div className="space-y-1">
                    <div className="text-xs text-muted-foreground">
                      {day.data.runs.length} run{day.data.runs.length > 1 ? 's' : ''}
                    </div>
                    <div className="text-xs font-medium">
                      {formatDistance(day.data.totalDistance)}
                    </div>
                    
                    {/* Individual run indicators - clickable */}
                    <div className="space-y-1 max-h-16 overflow-y-auto">
                      {day.data.runs.map((run: any, index: number) => (
                        <div
                          key={run.id}
                          className={`text-xs px-1 py-0.5 rounded cursor-pointer hover:opacity-80 transition-opacity ${getIntensityColor(run.run_type || 'easy')}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRunClick(run, day.data);
                          }}
                          title={`${run.run_type || 'easy'} - ${formatDistance(run.distance_meters || 0)}`}
                        >
                          {run.run_type || 'easy'}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Run Details Popup */}
      <Dialog open={isPopupOpen} onOpenChange={setIsPopupOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Run Details - {selectedRunData?.date ? new Date(selectedRunData.date).toLocaleDateString('en-US', { 
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) : ''}
            </DialogTitle>
          </DialogHeader>
          
          {selectedRunData && (
            <div className="space-y-6">
              {/* Primary Run Info */}
              <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-4">
                  <Badge className={`${getIntensityColor(selectedRunData.run.run_type || 'easy')} text-sm px-3 py-1`}>
                    {selectedRunData.run.run_type || 'easy'}
                  </Badge>
                  <div>
                    <div className="text-xl font-bold">
                      {formatDistance(selectedRunData.run.distance_meters || 0)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      in {formatTime(selectedRunData.run.time_seconds || 0)}
                    </div>
                  </div>
                </div>
                
                {selectedRunData.run.perceived_effort && (
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">{selectedRunData.run.perceived_effort}</div>
                    <div className="text-xs text-muted-foreground">RPE</div>
                  </div>
                )}
              </div>

              {/* Performance Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 border rounded-lg">
                  <Timer className="h-6 w-6 mx-auto mb-2 text-primary" />
                  <div className="font-semibold">
                    {selectedRunData.run.pace_per_km_seconds ? formatTime(selectedRunData.run.pace_per_km_seconds) : 'N/A'}
                  </div>
                  <div className="text-xs text-muted-foreground">Pace/km</div>
                </div>

                {selectedRunData.run.heart_rate_avg && (
                  <div className="text-center p-3 border rounded-lg">
                    <Heart className="h-6 w-6 mx-auto mb-2 text-red-500" />
                    <div className="font-semibold">{selectedRunData.run.heart_rate_avg}</div>
                    <div className="text-xs text-muted-foreground">Avg HR (bpm)</div>
                  </div>
                )}

                {selectedRunData.run.heart_rate_max && (
                  <div className="text-center p-3 border rounded-lg">
                    <Heart className="h-6 w-6 mx-auto mb-2 text-red-600" />
                    <div className="font-semibold">{selectedRunData.run.heart_rate_max}</div>
                    <div className="text-xs text-muted-foreground">Max HR (bpm)</div>
                  </div>
                )}

                {selectedRunData.run.elevation_gain_meters && (
                  <div className="text-center p-3 border rounded-lg">
                    <TrendingUp className="h-6 w-6 mx-auto mb-2 text-green-600" />
                    <div className="font-semibold">{selectedRunData.run.elevation_gain_meters}m</div>
                    <div className="text-xs text-muted-foreground">Elevation</div>
                  </div>
                )}
              </div>

              {/* Additional Details */}
              {(selectedRunData.run.zone_2_percentage || selectedRunData.run.weather_conditions || selectedRunData.run.notes) && (
                <div className="space-y-3">
                  <h4 className="font-semibold">Additional Information</h4>
                  
                  {selectedRunData.run.zone_2_percentage && (
                    <div className="flex justify-between items-center p-2 bg-muted/20 rounded">
                      <span className="text-sm">Zone 2 Percentage:</span>
                      <span className="font-medium">{selectedRunData.run.zone_2_percentage}%</span>
                    </div>
                  )}

                  {selectedRunData.run.weather_conditions && (
                    <div className="flex justify-between items-center p-2 bg-muted/20 rounded">
                      <span className="text-sm">Weather:</span>
                      <span className="font-medium">{selectedRunData.run.weather_conditions}</span>
                    </div>
                  )}

                  {selectedRunData.run.notes && (
                    <div className="p-3 bg-muted/20 rounded">
                      <span className="text-sm font-medium">Notes:</span>
                      <p className="text-sm mt-1">{selectedRunData.run.notes}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Day Summary if multiple runs */}
              {selectedRunData.dayData?.runs.length > 1 && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">Day Summary ({selectedRunData.dayData.runs.length} runs)</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-lg font-bold">
                        {formatDistance(selectedRunData.dayData.totalDistance)}
                      </div>
                      <div className="text-sm text-muted-foreground">Total Distance</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold">
                        {formatTime(selectedRunData.dayData.totalTime)}
                      </div>
                      <div className="text-sm text-muted-foreground">Total Time</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold">
                        {selectedRunData.dayData.avgPace ? formatTime(selectedRunData.dayData.avgPace) : 'N/A'}
                      </div>
                      <div className="text-sm text-muted-foreground">Avg Pace</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};